import { Button } from '@restomarker/ui-kit';
import { useSyncExternalStore, type ReactElement } from 'react';

import { t } from '@/shared/i18n';
import {
  currentForegroundPush,
  DEFAULT_PUSH_ROUTE,
  dismissForegroundPush,
  subscribeForegroundPush,
} from '@/shared/push';
import { Toast } from '@/shared/ui/Toast';

/** Текущий путь роутера — компонент живёт снаружи `RouterProvider`. */
export type RouterPath = {
  readonly get: () => string;
  readonly subscribe: (listener: () => void) => () => void;
};

/** На экране печати тост push не рисуется (T-047): там его место у результата печати. */
export const PRINT_PATH = '/kiosk/print';

export interface ForegroundPushToastProps {
  /** Открыть экран — роутер живёт снаружи провайдеров, поэтому переход передаётся сюда. */
  onOpen: (route: string) => void;
  path: RouterPath;
}

/**
 * Push, пришедший при открытом приложении (`shared/push/foreground`). Android его
 * в шторку не кладёт — показываем сами тем же тостом, что и результат печати.
 * `alert`, а не `status`: это не отклик на действие человека, а то, что нельзя пропустить.
 * На экране печати не рисуется (T-047).
 */
export function ForegroundPushToast({
  onOpen,
  path,
}: ForegroundPushToastProps): ReactElement | null {
  const push = useSyncExternalStore(subscribeForegroundPush, currentForegroundPush, () => null);
  const pathname = useSyncExternalStore(path.subscribe, path.get, path.get);
  // Поверх печати ничего не всплывает (решение владельца 5): тост встал бы на место
  // тоста результата печати с «Отменить», а стор держит одно сообщение — отмена
  // ошибочной этикетки пропала бы с экрана. О пришедшем говорит счётчик на колокольчике.
  if (!push || pathname === PRINT_PATH) return null;

  return (
    <Toast
      role="alert"
      onClose={dismissForegroundPush}
      actions={
        <Button
          onClick={() => {
            dismissForegroundPush();
            onOpen(DEFAULT_PUSH_ROUTE);
          }}
        >
          {t('push.open')}
        </Button>
      }
    >
      <b>{push.title}</b>
      <br />
      {push.body}
    </Toast>
  );
}
